import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon } from 'components/icons';
import Skeleton from '../skeleton/Skeleton';
import styles from './spinDetails.module.scss';
import { formatLocalTime } from 'utils/formatDate';
import cx from 'classnames';

interface DepositMatchCreditedDetailsProps {
  details: any;
  onBack: () => void;
  isLoading?: boolean;
  title?: string | null;
  message?: string | null;
}

const DepositMatchCreditedDetails: React.FC<DepositMatchCreditedDetailsProps> = ({
  details,
  title,
  message,
  onBack,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [isFadingOut, setIsFadingOut] = React.useState(false);

  const handleBack = () => {
    setIsFadingOut(true);
    setTimeout(() => {
      onBack();
    }, 200);
  };

  return (
    <div
      className={cx(styles.container, { [styles.fadingOut]: isFadingOut, [styles.deposit]: true })}
    >
      <button className={styles.backButton} onClick={handleBack} aria-label="Go back">
        <ChevronLeftIcon width="20" height="20" />
      </button>

      <div className={styles.header}>
        <h2>
          {isLoading ? <Skeleton width={180} height={24} /> : title || 'Deposit Bonus Credited'}
        </h2>
      </div>

      <div className={styles.statsList}>
        {isLoading || !details ? (
          <>
            <Skeleton width="100%" height={20} />
            <Skeleton width="80%" height={20} />
          </>
        ) : (
          <p
            style={{
              color: 'var(--text-secondary)',
              lineHeight: '1.5',
              margin: '0 0 16px 0',
              fontSize: '14px',
            }}
          >
            {message || 'Your deposit match bonus has been credited to your balance.'}
          </p>
        )}

        <div className={styles.statRowCard}>
          <span className={styles.statLabel}>
            {t('notifications.labels.creditedAmount', 'Credited Amount')}
          </span>
          {isLoading || !details ? (
            <Skeleton width={100} height={20} />
          ) : (
            <span className={styles.statValue}>
              {details.creditedAmount !== undefined
                ? `$${Number(details.creditedAmount).toFixed(2)}`
                : '-'}
            </span>
          )}
        </div>

        <div className={styles.statRowCard}>
          <span className={styles.statLabel}>
            {t('notifications.labels.bonusPercent', 'Bonus Percent')}
          </span>
          {isLoading || !details ? (
            <Skeleton width={80} height={20} />
          ) : (
            <span className={styles.statValue}>
              {details.percent !== undefined ? `${details.percent}%` : '-'}
            </span>
          )}
        </div>

        <div className={styles.statRowCard}>
          <span className={styles.statLabel}>{t('notifications.labels.currency', 'Currency')}</span>
          {isLoading || !details ? (
            <Skeleton width={60} height={20} />
          ) : (
            <span className={styles.statValue}>{details.currencyCode || 'USD'}</span>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        {isLoading || !details ? (
          <Skeleton width={160} height={16} />
        ) : (
          <span>
            {details.createdAt
              ? t('notifications.labels.credited', {
                  date: formatLocalTime(details.createdAt, false),
                })
              : t('notifications.labels.recently', 'Recently')}
          </span>
        )}
      </div>
    </div>
  );
};

export default DepositMatchCreditedDetails;
